import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Client call Oct 19 (José, Grupo Cruces) — ANGLE A: per-tenant CONTACT CADENCE
// policy for postventa outreach. Mirrors backend contact_cadence_policy.py:
//   mode cadence => N calls per contact day with gaps, next contact day every
//   `day_gap_days` (allowed weekdays), `contact_days_per_cycle` days per cycle,
//   `pause_days` pause, at most `max_cycles_per_due_service` cycles per due
//   service (oct20 CADENCE CAP, Sergio: default 2 for Grupo Cruces — after the
//   last cycle without contact the dialer stops until a NEW overdue service or
//   "Reanudar contacto" on the vehicle); the no-contestó WhatsApp goes after
//   the `wa_after_miss`-th MISSED call once per day (oct21, Roger: 3 for Grupo
//   Cruces; answered calls never count); the last WA of the cycle carries
//   the opt-out menu. mode legacy => today's behaviour.
// Admin/super_admin edit; every other role sees the effective policy read-only.
const WEEKDAYS = [
  { v: 0, l: 'Lun' }, { v: 1, l: 'Mar' }, { v: 2, l: 'Mié' }, { v: 3, l: 'Jue' },
  { v: 4, l: 'Vie' }, { v: 5, l: 'Sáb' }, { v: 6, l: 'Dom' },
];
const SOURCE_LABEL = {
  tenant_row: 'Configurada para esta agencia',
  tenant_default: 'Acuerdo del cliente (valor por defecto de esta agencia)',
  legacy_default: 'Comportamiento actual (sin cadencia configurada)',
};

export const policyToForm = (policy = {}) => ({
  mode: policy.mode === 'cadence' ? 'cadence' : 'legacy',
  calls_per_day: Number(policy.calls_per_day ?? 3),
  call_gap_minutes: Array.isArray(policy.call_gap_minutes) && policy.call_gap_minutes.length
    ? policy.call_gap_minutes.map(Number)
    : [15, 60],
  day_gap_days: Number(policy.day_gap_days ?? 2),
  contact_days_per_cycle: Number(policy.contact_days_per_cycle ?? 3),
  allowed_weekdays: Array.isArray(policy.allowed_weekdays) && policy.allowed_weekdays.length
    ? policy.allowed_weekdays.map(Number)
    : [0, 1, 2, 3, 4, 5],
  pause_days: Number(policy.pause_days ?? 15),
  // oct20 rename: read the new key, fall back to the pre-oct20 `max_cycles`.
  max_cycles_per_due_service: Number(policy.max_cycles_per_due_service ?? policy.max_cycles ?? 2),
  // oct21 (Roger): WA once per day after the THIRD missed call (was 2).
  wa_after_miss: Number(policy.wa_after_miss ?? 3),
  wa_once_per_day: policy.wa_once_per_day !== false,
  wa_optout_on_last: policy.wa_optout_on_last !== false,
});

// Human summary of the agreed day plan ("llamada 1 → +15 min → +60 min").
export const dayPlanText = (form) => {
  const n = Math.max(1, Number(form.calls_per_day) || 1);
  const gaps = form.call_gap_minutes || [];
  const parts = ['Llamada 1'];
  for (let i = 2; i <= n; i += 1) {
    const g = gaps[Math.min(i - 2, gaps.length - 1)] ?? gaps[gaps.length - 1] ?? 60;
    parts.push(`Llamada ${i} (+${g} min)`);
  }
  return parts.join(' → ');
};

function ContactCadencePolicyCard() {
  const { isAdmin } = useAuth();
  const canEdit = typeof isAdmin === 'function' ? isAdmin() : false;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(policyToForm({}));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/tenants/me/contact-cadence-policy');
      setData(res.data);
      setForm(policyToForm(res.data?.policy || {}));
    } catch (error) {
      // Older backend without the route: keep the card quiet (no console noise).
      if (!error?.response || error.response.status !== 404) {
        console.error('Error fetching contact cadence policy:', error);
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setGap = (idx, v) => setForm((prev) => {
    const gaps = [...prev.call_gap_minutes];
    gaps[idx] = Number(v) || 0;
    return { ...prev, call_gap_minutes: gaps };
  });
  const toggleWeekday = (d) => setForm((prev) => {
    const set = new Set(prev.allowed_weekdays);
    if (set.has(d)) set.delete(d); else set.add(d);
    return { ...prev, allowed_weekdays: Array.from(set).sort((a, b) => a - b) };
  });

  const gapCount = Math.max(0, (Number(form.calls_per_day) || 1) - 1);
  const gapsForUi = useMemo(() => {
    const out = [];
    for (let i = 0; i < gapCount; i += 1) {
      out.push(form.call_gap_minutes[i] ?? form.call_gap_minutes[form.call_gap_minutes.length - 1] ?? 60);
    }
    return out;
  }, [form.call_gap_minutes, gapCount]);

  const save = async () => {
    try {
      setSaving(true);
      const payload = form.mode === 'cadence'
        ? { policy: { ...form, call_gap_minutes: gapsForUi } }
        : { policy: { mode: 'legacy' } };
      const res = await api.put('/tenants/me/contact-cadence-policy', payload);
      setData((prev) => ({ ...(prev || {}), ...res.data }));
      setForm(policyToForm(res.data?.policy || {}));
      toast.success('Cadencia de contacto guardada');
    } catch (error) {
      console.error('Error saving contact cadence policy:', error);
      toast.error('No se pudo guardar la cadencia de contacto');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('¿Restablecer la cadencia al valor por defecto de esta agencia?')) return;
    try {
      setSaving(true);
      const res = await api.put('/tenants/me/contact-cadence-policy', { reset: true });
      setData((prev) => ({ ...(prev || {}), ...res.data }));
      setForm(policyToForm(res.data?.policy || {}));
      toast.success('Cadencia restablecida');
    } catch (error) {
      console.error('Error resetting contact cadence policy:', error);
      toast.error('No se pudo restablecer la cadencia');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="contact-cadence-card">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!data) return null;

  const rows = data.rows || [];
  const inputCls = 'input-field mt-1';

  return (
    <div className="card" data-testid="contact-cadence-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-foreground flex items-center">
          <ClockIcon className="h-5 w-5 mr-2 text-primary-600" />
          Cadencia de contacto (Postventa)
        </h3>
        <span className="text-xs rounded-full px-2 py-0.5 bg-gray-100 dark:bg-white/10" style={{ color: 'var(--muted)' }} data-testid="cadence-source">
          {SOURCE_LABEL[data.source] || data.source}
        </span>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
        Define cómo la automatización insiste con un cliente de Postventa que no contesta: llamadas por día de
        contacto, espera entre llamadas, patrón de días, pausa y el WhatsApp de “no contestó”. La persona responsable
        lanza el contacto desde Postventa; la automatización sigue esta cadencia hasta lograr contacto, cita o baja.
        Las campañas ya lanzadas conservan la cadencia con la que se lanzaron.
      </p>

      {/* Effective policy (every role) */}
      <div className="rounded-lg border border-border p-3 mb-4" data-testid="cadence-effective">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-border last:border-b-0">
                <td className="py-1 pr-3 font-medium text-foreground whitespace-nowrap">{row.label}</td>
                <td className="py-1 text-foreground">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {form.mode === 'cadence' && (
          <p className="mt-2 text-xs" style={{ color: 'var(--muted)' }} data-testid="cadence-day-plan">
            Plan de un día de contacto: {dayPlanText({ ...form, call_gap_minutes: gapsForUi })}
            {form.wa_after_miss ? ` · WhatsApp “no contestó” tras la llamada ${form.wa_after_miss} sin respuesta del día` : ''}
          </p>
        )}
      </div>

      {canEdit && (
        <div className="space-y-4" data-testid="cadence-editor">
          <div>
            <label htmlFor="cadence_mode" className="block text-sm font-medium text-foreground">Modo</label>
            <select id="cadence_mode" className={inputCls} value={form.mode} onChange={(e) => setField('mode', e.target.value)}>
              <option value="cadence">Cadencia por agencia (llamadas por día, patrón de días, pausa)</option>
              <option value="legacy">Heredado (comportamiento actual de la campaña)</option>
            </select>
          </div>

          {form.mode === 'cadence' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="cadence_calls_per_day" className="block text-sm font-medium text-foreground">Llamadas por día de contacto</label>
                  <input id="cadence_calls_per_day" type="number" min={1} max={5} className={inputCls}
                    value={form.calls_per_day} onChange={(e) => setField('calls_per_day', Number(e.target.value))} />
                </div>
                <div>
                  <label htmlFor="cadence_day_gap" className="block text-sm font-medium text-foreground">Cada cuántos días (patrón)</label>
                  <input id="cadence_day_gap" type="number" min={1} max={7} className={inputCls}
                    value={form.day_gap_days} onChange={(e) => setField('day_gap_days', Number(e.target.value))} />
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>2 = un día sí y uno no (p. ej. lun/mié/vie).</p>
                </div>
                <div>
                  <label htmlFor="cadence_days_cycle" className="block text-sm font-medium text-foreground">Días de contacto por ciclo (semana)</label>
                  <input id="cadence_days_cycle" type="number" min={1} max={7} className={inputCls}
                    value={form.contact_days_per_cycle} onChange={(e) => setField('contact_days_per_cycle', Number(e.target.value))} />
                </div>
              </div>

              <div>
                <span className="block text-sm font-medium text-foreground">Espera entre llamadas del mismo día (minutos)</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-1">
                  {gapsForUi.map((g, idx) => (
                    <div key={`gap-${idx}`}>
                      <label htmlFor={`cadence_gap_${idx}`} className="block text-xs" style={{ color: 'var(--muted)' }}>
                        Antes de la llamada {idx + 2}
                      </label>
                      <input id={`cadence_gap_${idx}`} type="number" min={5} max={240} className={inputCls}
                        value={g} onChange={(e) => setGap(idx, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-sm font-medium text-foreground">Días permitidos</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {WEEKDAYS.map((d) => {
                    const on = form.allowed_weekdays.includes(d.v);
                    return (
                      <button key={d.v} type="button" onClick={() => toggleWeekday(d.v)}
                        aria-pressed={on}
                        className={`px-3 py-1 rounded-full text-sm border ${on ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300' : 'border-border'}`}
                        style={on ? undefined : { color: 'var(--muted)' }}>
                        {d.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="cadence_pause" className="block text-sm font-medium text-foreground">Pausa tras el ciclo sin contacto (días)</label>
                  <input id="cadence_pause" type="number" min={0} max={90} className={inputCls}
                    value={form.pause_days} onChange={(e) => setField('pause_days', Number(e.target.value))} />
                </div>
                <div>
                  <label htmlFor="cadence_max_cycles" className="block text-sm font-medium text-foreground">Ciclos máximos por servicio vencido (0 = sin límite)</label>
                  <input id="cadence_max_cycles" type="number" min={0} max={12} className={inputCls}
                    value={form.max_cycles_per_due_service} onChange={(e) => setField('max_cycles_per_due_service', Number(e.target.value))} />
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }} data-testid="cadence-max-cycles-help">
                    Tras {form.max_cycles_per_due_service || 'N'} ciclos sin contacto la automatización se detiene para ese servicio
                    vencido; vuelve solo con un nuevo servicio vencido o con “Reanudar contacto” en el vehículo.
                  </p>
                </div>
                <div>
                  <label htmlFor="cadence_wa_after" className="block text-sm font-medium text-foreground">WhatsApp “no contestó” tras la llamada n.º sin respuesta</label>
                  <input id="cadence_wa_after" type="number" min={1} max={Math.max(1, Number(form.calls_per_day) || 1)} className={inputCls}
                    value={form.wa_after_miss} onChange={(e) => setField('wa_after_miss', Number(e.target.value))} />
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }} data-testid="cadence-wa-after-help">
                    Solo cuentan las llamadas no contestadas del día (una llamada contestada no cuenta): el WhatsApp sale una vez
                    al día tras la llamada {form.wa_after_miss || 'N'} sin respuesta. Máximo: las llamadas por día ({Math.max(1, Number(form.calls_per_day) || 1)}).
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <label className="inline-flex items-center text-sm text-foreground">
                  <input type="checkbox" className="mr-2" checked={form.wa_once_per_day}
                    onChange={(e) => setField('wa_once_per_day', e.target.checked)} />
                  WhatsApp “no contestó” solo una vez por día
                </label>
                <label className="inline-flex items-center text-sm text-foreground">
                  <input type="checkbox" className="mr-2" checked={form.wa_optout_on_last}
                    onChange={(e) => setField('wa_optout_on_last', e.target.checked)} />
                  El último WhatsApp del ciclo incluye el menú de baja
                </label>
              </div>
              {form.wa_optout_on_last && data.optout_menu_text && (
                <p className="text-xs rounded-lg border border-border p-2" style={{ color: 'var(--muted)' }} data-testid="cadence-optout-text">
                  Menú de baja: “{data.optout_menu_text}”. La opción 3 marca al cliente como <strong>no contactar</strong> (Puede
                  contactar = NO) y detiene la automatización. Requiere la plantilla aprobada en WhatsApp → Configuración →
                  “No contestó · último de la semana (menú de baja)”.
                </p>
              )}
            </>
          )}

          <div className="flex items-center gap-3">
            <button type="button" className="btn-primary" onClick={save} disabled={saving} data-testid="cadence-save">
              {saving ? 'Guardando…' : 'Guardar cadencia'}
            </button>
            <button type="button" className="btn-secondary" onClick={reset} disabled={saving} data-testid="cadence-reset">
              Restablecer al valor por defecto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactCadencePolicyCard;
